import '../App.css';
import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import SummaryBox from '../components/SummaryBox';
import Chat from '../components/ChatBotAssistant';
import {
  useFetchTestsByText,
  useFetchTestsByTags,
  useFetchTestsByTestTool,
} from '../hooks/useServices';

function TestCatalogPage() {
  // Ensure "List All Tests" when entering
  useEffect(() => {
    handleAllTestsClick();
  }, []);

  // Hook for text-based search
  const {
    tests: textTests,
    loading: textLoading,
    showLoadingMessage: textShowLoadingMessage,
    error: textError,
    fetchTests: textFetchTests,
    clearTests: textClearTests,
  } = useFetchTestsByText();

  // Hook for tag-based search
  const {
    tests: tagTests,
    loading: tagLoading,
    showLoadingMessage: tagShowLoadingMessage,
    error: tagError,
    fetchTests: tagFetchTests,
    clearTests: tagClearTests,
  } = useFetchTestsByTags();

  // Hook for testTool-based search
  const {
    tests: toolTests,
    loading: toolLoading,
    showLoadingMessage: toolShowLoadingMessage,
    error: toolError,
    fetchTests: toolFetchTests,
    clearTests: toolClearTests,
  } = useFetchTestsByTestTool();

  const [summaryTitle, setSummaryTitle] = useState('Summary');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTool, setSelectedTool] = useState('');

  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('text'); // 'text', 'tag' or 'tool'
  const [expandedIndexes, setExpandedIndexes] = useState([]);
  const [expanding, setExpanding] = useState(false);
  const [expandingAction, setExpandingAction] = useState(''); // 'expanding' or 'collapsing'
  const [showExpandingMessage, setShowExpandingMessage] = useState(false);
  const [queryTooShort] = useState(false);

  // Choose which hook’s data to display
  const tests =
    mode === 'text' ? textTests : mode === 'tag' ? tagTests : toolTests;
  const loading =
    mode === 'text' ? textLoading : mode === 'tag' ? tagLoading : toolLoading;
  const showLoadingMessage =
    mode === 'text'
      ? textShowLoadingMessage
      : mode === 'tag'
        ? tagShowLoadingMessage
        : toolShowLoadingMessage;
  const error =
    mode === 'text' ? textError : mode === 'tag' ? tagError : toolError;

  const handleTagSearch = (tags) => {
    setMode('tag');
    setSelectedTags(tags || []);
    setSelectedTool('');
    setSummaryTitle(
      tags && tags.length ? `Summary for: ${tags.join(', ')}` : 'Summary'
    );
    tagFetchTests(tags);
  };

  const handleToolSearch = (searchTool) => {
    setMode('tool');
    setSelectedTool(searchTool);
    setSelectedTags([]);
    setSummaryTitle(`Summary for ${searchTool} tests`);
    toolFetchTests(searchTool);
  };

  const handleToggle = (index) => {
    if (expandedIndexes.includes(index)) {
      setExpandedIndexes(expandedIndexes.filter((i) => i !== index));
    } else {
      setExpandedIndexes([...expandedIndexes, index]);
    }
  };

  const handleSearchFromNav = (searchText, context) => {
    if (context === 'catalog') {
      setQuery(searchText);
      if (searchText.length < 3) return;
      setMode('text');
      textFetchTests(searchText);
      setSummaryTitle(`Summary for “${searchText}”`);
    }
  };

  const handleExpandCollapseAll = () => {
    const isExpanding = expandedIndexes.length !== tests.length;
    setExpandingAction(isExpanding ? 'expanding' : 'collapsing');
    setShowExpandingMessage(true);
    setExpanding(true);

    setTimeout(() => {
      setExpandedIndexes(isExpanding ? tests.map((_, index) => index) : []);
      setExpanding(false);
      setTimeout(() => setShowExpandingMessage(false), 300);
    }, 300);
  };

  // all queries for the left panel
  const handleAllTestsClick = () => handleTagSearch(); //simply no tag
  const handleSAPTestsClick = () => handleTagSearch(['sles4sap']);
  const handleKernelTestsClick = () => handleTagSearch(['kernel']);
  const handleHATestsClick = () => handleTagSearch(['ha']);
  const handleNetworkTestsClick = () => handleTagSearch(['network']);
  const handlePubliccloudTestsClick = () => handleTagSearch(['publiccloud']);
  const handleHPCTestsClick = () => handleTagSearch(['hpc']);
  const handleAutoyastTestsClick = () => handleTagSearch(['autoyast']);
  const handleInstallationTestsClick = () => handleTagSearch(['installation']);
  const handleContainersTestsClick = () => handleTagSearch(['containers']);
  const handleVirtualizationTestsClick = () =>
    handleTagSearch(['virtualization']);
  const handleConsoleTestsClick = () => handleTagSearch(['console']);
  const handleMicroosTestsClick = () => handleTagSearch(['microos']);
  const handleMigrationTestsClick = () => handleTagSearch(['migration']);
  const handleOnlineMigrationTestsClick = () =>
    handleTagSearch(['online_migration']);
  const handleSleposTestsClick = () => handleTagSearch(['slepos']);
  const handleSystemdTestsClick = () => handleTagSearch(['systemd_testsuite']);
  const handleVirtAutotestTestsClick = () => handleTagSearch(['virt_autotest']);

  // all queries for the right panel
  const handleOSADOTestsClick = () => handleToolSearch('osado');
  const handleLTPTestsClick = () => handleToolSearch('ltp');
  const handleXfstestsTestsClick = () => handleToolSearch('xfstests');
  const handleBlktestsTestsClick = () => handleToolSearch('blktests');

  const handleClearData = () => {
    if (mode === 'text') {
      textClearTests();
    } else if (mode === 'tag') {
      tagClearTests();
    } else if (mode === 'tool') {
      toolClearTests();
    }
    setMode('text');
    setQuery('');
    setSelectedTags([]);
    setSelectedTool('');
    setSummaryTitle('Summary');
  };

  return (
    <div>
      <Navigation onSearch={(query) => handleSearchFromNav(query, 'catalog')} />
      <div className="container-fluid custom-container">
        <div className="row">
          <div className="col-md-2">
            <div className="section-left">
              <h4>Test by Functional Areas</h4>
              <div className="d-flex mb-3 flex-wrap ">
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleAllTestsClick}
                >
                  List All Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleSAPTestsClick}
                >
                  SLES4SAP Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleKernelTestsClick}
                >
                  Kernel Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleHATestsClick}
                >
                  HA Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleNetworkTestsClick}
                >
                  Network Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handlePubliccloudTestsClick}
                >
                  Public Cloud Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleHPCTestsClick}
                >
                  HPC Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleAutoyastTestsClick}
                >
                  Autoyast Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleInstallationTestsClick}
                >
                  Installation Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleContainersTestsClick}
                >
                  Containers Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleVirtualizationTestsClick}
                >
                  Virtualization Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleConsoleTestsClick}
                >
                  Console Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleMicroosTestsClick}
                >
                  Microos Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleMigrationTestsClick}
                >
                  Migration Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleOnlineMigrationTestsClick}
                >
                  Online Migration Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleSleposTestsClick}
                >
                  Slepos Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleSystemdTestsClick}
                >
                  Systemd Tests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleVirtAutotestTestsClick}
                >
                  VirtAutotest Tests
                </button>
              </div>
            </div>
          </div>

          {loading}
          {error}
          {expanding}

          {queryTooShort && (
            <div className="alert alert-warning mt-2" role="alert">
              Please enter at least 3 characters to search.
            </div>
          )}

          <div className="col-md-8">
            <div style={{ minHeight: '65px' }}>
              {showLoadingMessage && (
                <div className="mb-3 px-3 py-2 border rounded bg-light d-flex align-items-center justify-content-center">
                  <strong className="me-2">Loading test cases...</strong>
                  <div
                    className="spinner-border text-success"
                    role="status"
                    aria-hidden="true"
                  ></div>
                </div>
              )}

              {showExpandingMessage && (
                <div className="mb-3 px-3 py-2 border rounded bg-light d-flex align-items-center justify-content-center">
                  <strong className="me-2">
                    {expandingAction === 'expanding'
                      ? 'Expanding all tests...'
                      : 'Collapsing all tests...'}
                  </strong>
                  <div
                    className="spinner-border text-secondary"
                    role="status"
                    aria-hidden="true"
                  ></div>
                </div>
              )}
            </div>

            <div className="section-center">
              {(tests.length > 0 || loading) && (
                <div className="d-flex justify-content-end mt-2 mb-3">
                  <button
                    className="btn btn-danger me-2"
                    onClick={handleClearData}
                    disabled={loading}
                  >
                    Clear Listed Tests
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleExpandCollapseAll}
                    disabled={loading}
                  >
                    {expandedIndexes.length === tests.length
                      ? 'Collapse All'
                      : 'Expand All'}
                  </button>
                </div>
              )}

              <SummaryBox
                tests={tests}
                title={summaryTitle}
                loading={loading}
              />
              <div className="tests-list-container">
                <ul className="list-group">
                  {tests.map((test, index) => (
                    <li
                      key={index}
                      className={`list-group-item ${expandedIndexes.includes(index) ? 'list-group-item-expanded' : ''}`}
                      onClick={() => handleToggle(index)}
                    >
                      <div className="list-item-header">
                        <strong style={{ fontSize: '20px' }}>
                          {test.testTool} : {test.parent} : {test.testName}
                        </strong>
                        <span>
                          {expandedIndexes.includes(index) ? '↓' : '→'}

                          {/* Arrow changes when expanded */}
                        </span>
                      </div>

                      <div
                        className={`expandable mt-2 ${expandedIndexes.includes(index) ? 'open' : 'closed'}`}
                      >
                        {expandedIndexes.includes(index) && (
                          <div>
                            <strong>AI-Assisted Summary:</strong>
                            {test.aiAssistedSummary ? (
                              <p>{test.aiAssistedSummary}</p>
                            ) : (
                              <p className="text-muted">
                                No AI summary available.
                              </p>
                            )}
                            <strong>Test Description:</strong>
                            <p className="test-description">{test.comments}</p>
                            <strong>
                              The Last change (the last git commit date):
                            </strong>
                            <p>{test.lastCommitDate}</p>
                            <strong>Initial git commit date:</strong>
                            <p>{test.firstCommitDate}</p>
                            <strong>Maintainers:</strong>
                            {test.maintainer && test.maintainer.length > 0 ? (
                              <ul className="maintainers-list">
                                {test.maintainer.map(
                                  (maintainer, maintainerIndex) => (
                                    <li
                                      key={maintainerIndex}
                                      className="maintainer-item"
                                    >
                                      {maintainer}
                                    </li>
                                  )
                                )}
                              </ul>
                            ) : (
                              <p className="text-muted">None found</p>
                            )}
                            <strong>Associated tags:</strong>
                            {test.tags && test.tags.length > 0 && (
                              <ul className="tags-list">
                                {test.tags.map((tag, tagIndex) => (
                                  <li key={tagIndex} className="tag-item">
                                    {tag}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <strong>License:</strong>
                            <p>{test.licenses}</p>
                            <strong>
                              <a
                                href={test.fullPath}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Source code
                              </a>
                            </strong>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="col-md-2">
            <div className="section-right">
              <h4>Tools</h4>
              <div className="d-flex mb-3 flex-wrap">
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleOSADOTestsClick}
                >
                  OSADO
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleLTPTestsClick}
                >
                  LTP
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleXfstestsTestsClick}
                >
                  xfstests
                </button>
                <button
                  className="btn btn-success mb-2 w-100"
                  onClick={handleBlktestsTestsClick}
                >
                  blktests
                </button>
              </div>
            </div>
          </div>
        </div>
        <Chat />
      </div>

      <div className="col-md-8"></div>
      <Footer />
    </div>
  );
}

export default TestCatalogPage;
